import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
   Box,
   Flex,
   Heading,
   ScrollArea,
   Table,
   Button,
   Text,
   Spinner,
   Checkbox,
   TextField,
   Dialog,
} from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import { Paginator } from 'shared/components/pagination';
import { ArrowDownwardIcon, ArrowUpwardIcon } from 'shared/icons';
import { TableLoaderOverlay } from 'shared/components';

import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import APSidebar from 'Components/adminpanel/APSidebar/APSidebar';

import './blogs-manager.scss';
import { BlogsActionsMenu } from './blogs-actions-menu';
import { useBlogsManager } from './blogs-manager.provider';
import { BlogsMangerFilters } from './blogs-manger-filters';
import { DateUtilsService } from 'shared/utils/date-utils.service';
import { BlogsConfirmDialog } from './blogs-confirm-dialog';
import { toast } from 'sonner';

type BlogsManagerAction = 'ACTIVATE_ALL' | 'DEACTIVATE_ALL' | 'DELETE_ALL' | 'EMPTY';

function BlogsManagerView() {
   const navigate = useNavigate();
   const blogsManager = useBlogsManager();

   const [open, setOpen] = React.useState<boolean>(false);
   const [action, setAction] = React.useState<BlogsManagerAction>('EMPTY');

   React.useEffect(() => {
      blogsManager.loadBlogs();
   }, []);

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="blogs-manager">
            <Flex width="270px" minWidth="270px">
               <APSidebar />
            </Flex>
            <Box className="slider-manager__content">
               <Flex py="1" mx="3" justify="between">
                  <Heading as="h1" weight="medium">
                     Blog List
                  </Heading>
                  <Button size="3" onClick={() => navigate('/admin/blogs/addblogs')}>
                     Add Blog
                  </Button>
               </Flex>

               <BlogsMangerFilters />

               <ScrollArea scrollbars="both" type="always" className="blogs-manager__scroll-area">
                  <Box p="3">
                     <Table.Root size="3" layout="auto" variant="surface">
                        <Table.Header>
                           <Table.Row>
                              <Table.ColumnHeaderCell width="80px" maxWidth="80px" align="center">
                                 <Flex gap="1" justify="center">
                                    <Checkbox
                                       size="3"
                                       color="blue"
                                       checked={blogsManager.selectedAll}
                                       onCheckedChange={(cheked) => {
                                          if (cheked) {
                                             blogsManager.onSelectAll();
                                          } else {
                                             blogsManager.onDeselectAll();
                                          }
                                       }}
                                       highContrast
                                    ></Checkbox>
                                 </Flex>
                              </Table.ColumnHeaderCell>

                              <Table.ColumnHeaderCell>
                                 <Flex gap="1" align="center">
                                    <span className="header-label">Title</span>
                                    <button
                                       className="sort-trigger"
                                       onClick={() => {
                                          blogsManager.onSortBy('title');
                                       }}
                                    >
                                       {
                                          {
                                             asc: <ArrowDownwardIcon width={18} height={18} />,
                                             desc: <ArrowUpwardIcon width={18} height={18} />,
                                          }[blogsManager.direction]
                                       }
                                    </button>
                                 </Flex>
                              </Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>
                                 <Flex gap="1" align="center">
                                    <span className="header-label">Created</span>
                                    <button
                                       className="sort-trigger"
                                       onClick={() => {
                                          blogsManager.onSortBy('created');
                                       }}
                                    >
                                       {
                                          {
                                             asc: <ArrowDownwardIcon width={18} height={18} />,
                                             desc: <ArrowUpwardIcon width={18} height={18} />,
                                          }[blogsManager.direction]
                                       }
                                    </button>
                                 </Flex>
                              </Table.ColumnHeaderCell>

                              <Table.ColumnHeaderCell justify="center">
                                 Action
                              </Table.ColumnHeaderCell>
                           </Table.Row>
                        </Table.Header>

                        <Table.Body>
                           <TableLoaderOverlay
                              show={blogsManager.loadBlogsState.status === 'pending'}
                              colSpan={4}
                           />

                           {blogsManager.blogs.map((blog) => {
                              return (
                                 <Table.Row key={blog.id}>
                                    <Table.Cell width="80px" justify="center">
                                       <Checkbox
                                          color="blue"
                                          size="2"
                                          highContrast
                                          checked={blogsManager.idsArray.includes(blog.id)}
                                          onCheckedChange={() => {
                                             blogsManager.onToggleById(blog.id);
                                          }}
                                       />
                                    </Table.Cell>

                                    <Table.Cell>{blog.title}</Table.Cell>

                                    <Table.Cell width="220px">
                                       {DateUtilsService.fromUnixToAppFormat(blog.created)}
                                    </Table.Cell>

                                    <Table.Cell
                                       className="action-cell"
                                       justify="center"
                                       width="150px"
                                    >
                                       <BlogsActionsMenu blog={blog} />
                                    </Table.Cell>
                                 </Table.Row>
                              );
                           })}
                        </Table.Body>
                     </Table.Root>
                  </Box>
               </ScrollArea>

               <Flex className="slider-manager__pagination" py="2" px="3">
                  <Flex gap="4" px="3">
                     <Button
                        variant="surface"
                        size="3"
                        color="blue"
                        highContrast
                        disabled={blogsManager.diasableDeactivateAll}
                        onClick={() => {
                           setOpen(true);
                           setAction('ACTIVATE_ALL');
                        }}
                     >
                        <Text weight="regular" size="2">
                           ACTIVATE
                        </Text>
                     </Button>

                     <Button
                        variant="surface"
                        size="3"
                        color="blue"
                        highContrast
                        disabled={blogsManager.diasableDeactivateAll}
                        onClick={() => {
                           setOpen(true);
                           setAction('DEACTIVATE_ALL');
                        }}
                     >
                        <Text weight="regular" size="2">
                           DEACTIVATE
                        </Text>
                     </Button>

                     <Button
                        size="3"
                        disabled={blogsManager.diasableDeactivateAll}
                        onClick={() => {
                           setOpen(true);
                           setAction('DELETE_ALL');
                        }}
                     >
                        <Text weight="regular" size="2">
                           DELETE
                        </Text>
                     </Button>
                  </Flex>

                  <Paginator
                     totalPages={blogsManager.totalPages}
                     currentPage={blogsManager.pagination.page}
                     onPageClick={(p) => {
                        blogsManager.pagination.setPage(p);
                        const viewPage = p + 1;
                        blogsManager.onToPageChange(viewPage.toString());
                     }}
                     setRange={(p) => {
                        blogsManager.pagination.setRange(p);
                     }}
                     range={blogsManager.pagination.range}
                  />

                  <Flex align="center" gap="2">
                     <Flex align="center" gap="1">
                        <Text size="3" color="gray">
                           Total pages:
                        </Text>
                        <Text size="3" weight="medium">
                           {blogsManager.totalPages === 0 ? '' : blogsManager.totalPages}
                        </Text>
                     </Flex>

                     <TextField.Root
                        value={blogsManager.toPage}
                        onChange={(e) => {
                           blogsManager.onToPageChange(e.target.value);
                        }}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              blogsManager.goToPage();
                           }
                        }}
                        size="3"
                        color="blue"
                        type="number"
                        max={blogsManager.totalPages}
                     ></TextField.Root>
                  </Flex>
               </Flex>
            </Box>
         </Box>

         <Dialog.Root
            open={open}
            onOpenChange={(o) => {
               if (!o) {
                  setAction('EMPTY');
               }
               setOpen(o);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(e) => e.preventDefault()}
            >
               {
                  {
                     DEACTIVATE_ALL: (
                        <BlogsConfirmDialog
                           title="Deactivate Blogs?"
                           message="Do you want to Deactivate these Blogs?"
                           loading={blogsManager.deactivateAllBlogState.status === 'pending'}
                           onConfirm={() => {
                              blogsManager.deactivateAll({
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success('Blogs Deactivated successfully!');
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     ACTIVATE_ALL: (
                        <BlogsConfirmDialog
                           title="Activate Blogs?"
                           message="Do you want to Activate these Blogs?"
                           loading={blogsManager.activateAllBlogState.status === 'pending'}
                           onConfirm={() => {
                              blogsManager.activateAll({
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success('Blogs Activated successfully!');
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     DELETE_ALL: (
                        <BlogsConfirmDialog
                           title="Delete Blogs?"
                           message="Do you want to Delete Blogs?"
                           loading={blogsManager.deleteAllBlogState.status === 'pending'}
                           onConfirm={() => {
                              blogsManager.deleteAll({
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success('Blogs Deleted successfully!');
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     EMPTY: <></>,
                  }[action]
               }
            </Dialog.Content>
         </Dialog.Root>
      </>
   );
}

export const BlogsManager = observer(BlogsManagerView);
